import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import type { Academy, Coach, Location, Student, User } from '@court-sync/shared';
import { LEGAL_VERSIONS } from '@court-sync/shared';
import { academyService } from '../../../services/academy';
import { getPublicLocations } from '../../../services/location';
import { academyProfileService } from '../../../services/academyProfile';
import { AuthService } from '../../../services/auth';
import { buildEmailVerificationPageUrl } from '../../../utils/emailVerificationRedirect';
import { privateBookingService, type PrivateBookingConfig } from '../../../services/privateBooking';
import { studentService } from '../../../services/student';
import { useAuthStore } from '../../../store/auth';
import { Card, CardContent, CardDescription, CardHeader } from '../../../components/ui/Card';
import { Button } from '@/components/ui';
import { userFormSchema, type UserFormData } from '../publicBooking/schemas';
import { AcademyGuestRegistrationBlock } from '../publicBooking/AcademyGuestRegistrationBlock';
import { AcademyBrandingProvider } from '../../../contexts/AcademyBrandingContext';
import { PublicAcademyContactFooter } from '../../../components/public/PublicAcademyContactFooter';
import { LogIn } from 'lucide-react';
import { PrivateBookingWizard } from './PrivateBookingWizard';
import { privateBookingRegisterPath } from '../publicBooking/bookingPaths';
import { useAcademyPaths } from '../../../hooks/useAcademyPaths';
import { useAcademySlug } from '../../../hooks/useAcademySlug';
import { useAcademyHost } from '../../../contexts/AcademyHostContext';
import { isStarterRequestBookingAcademy } from '../../../utils/publicBrowseMode';
import { StarterMemberAuthNotFound } from '../../../components/public/StarterMemberAuthNotFound';

type PrivateBookingPublicLayoutProps = {
  academy: Academy;
  user: User | null;
  showMultiLocationPublic: boolean;
  bannerError: boolean;
  onBannerError: () => void;
  children: React.ReactNode;
};

function PrivateBookingPublicLayout({
  academy,
  user,
  showMultiLocationPublic,
  bannerError,
  onBannerError,
  children,
}: PrivateBookingPublicLayoutProps) {
  const navigate = useNavigate();
  const { p, login } = useAcademyPaths(academy);
  const loginReturn = privateBookingRegisterPath(academy);
  const hasBanner = !!(academy.bannerUrl && !bannerError);

  return (
    <AcademyBrandingProvider primaryColor={academy.primaryColor} secondaryColor={academy.secondaryColor}>
      <div className="flex min-h-screen flex-col bg-background">
        <nav className="border-b border-border bg-card/80 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/70">
          <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
            <Link to={p('')} className="flex min-w-0 items-center gap-2">
              {academy.logoUrl ? (
                <img
                  src={academy.logoUrl}
                  alt={academy.name}
                  className="h-9 w-auto max-w-[200px] object-contain md:h-11"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <span className="truncate text-lg font-bold tracking-tight text-foreground md:text-xl">{academy.name}</span>
              )}
            </Link>
            <div className="flex shrink-0 items-center gap-2 sm:gap-3">
              <Button
                variant="outline"
                size="sm"
                className="hidden sm:inline-flex"
                onClick={() => navigate(p('/lessons'))}
              >
                View lessons
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="sm:hidden"
                onClick={() => navigate(p('/lessons'))}
              >
                Lessons
              </Button>
              {user?.roles?.includes('guardian') || user?.roles?.includes('student') ? (
                <Button
                  size="sm"
                  className="gap-1.5"
                  style={academy.primaryColor ? { backgroundColor: academy.primaryColor } : undefined}
                  onClick={() => navigate(p('/dashboard'))}
                >
                  <LogIn className="h-4 w-4" aria-hidden />
                  <span className="hidden sm:inline">Dashboard</span>
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="gap-1.5"
                  style={academy.primaryColor ? { backgroundColor: academy.primaryColor } : undefined}
                  onClick={() =>
                    navigate(login(loginReturn))
                  }
                >
                  <LogIn className="h-4 w-4" aria-hidden />
                  <span className="hidden sm:inline">Sign in</span>
                </Button>
              )}
            </div>
          </div>
        </nav>

        <section className="relative isolate overflow-hidden" aria-labelledby="private-booking-hero-heading">
          <div className="absolute inset-0">
            {hasBanner ? (
              <img
                src={academy.bannerUrl}
                alt=""
                className="h-full w-full object-cover object-center"
                referrerPolicy="no-referrer"
                onError={onBannerError}
              />
            ) : (
              <div
                className="h-full min-h-[200px] w-full bg-gradient-to-br from-primary/25 via-primary/10 to-muted/40 md:min-h-[240px]"
                aria-hidden
              />
            )}
          </div>
          {hasBanner && <div className="absolute inset-0 bg-black/10" aria-hidden />}
          <div className="relative z-10 mx-auto max-w-7xl px-4 pb-10 pt-8 sm:px-6 sm:pb-12 sm:pt-10 lg:px-8">
            <div
              className={
                hasBanner
                  ? 'max-w-3xl rounded-xl bg-background/95 p-6 shadow-sm backdrop-blur-sm sm:p-8'
                  : 'max-w-3xl'
              }
            >
              <Link
                to={p('')}
                className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                ← Academy home
              </Link>
              <h1
                id="private-booking-hero-heading"
                className="mt-4 max-w-3xl text-3xl font-bold tracking-tight text-foreground md:text-4xl"
              >
                Book a private lesson
              </h1>
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
                Choose your coach and weekly times that work for you — we’ll price it from their hourly rate. Tap the
                schedule to build one or more recurring slots.
              </p>
            </div>
          </div>
        </section>

        <div className="mx-auto flex w-full max-w-screen-2xl flex-1 flex-col px-4 py-10 sm:px-6 sm:py-12 lg:px-8">{children}</div>

        <PublicAcademyContactFooter academy={academy} showMultiLocationPublic={showMultiLocationPublic} />
      </div>
    </AcademyBrandingProvider>
  );
}

export default function PublicPrivateBookingPage() {
  const academySlug = useAcademySlug();
  const { host, academy: hostAcademy, loading: hostLoading } = useAcademyHost();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const setAuthUser = useAuthStore((s) => s.setUser);

  const registerForm = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      phone: '',
      termsAccepted: false,
      privacyAccepted: false,
      marketingEmailConsent: false,
    },
  });

  const [registerSubmitting, setRegisterSubmitting] = useState(false);

  const [academy, setAcademy] = useState<Academy | null>(null);
  const { p, login } = useAcademyPaths(academy);
  const [config, setConfig] = useState<PrivateBookingConfig | null>(null);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [publicLocations, setPublicLocations] = useState<Location[]>([]);
  const [bannerError, setBannerError] = useState(false);

  const showMultiLocationPublic = useMemo(
    () => publicLocations.length >= 2 && !!academy,
    [publicLocations.length, academy]
  );

  const [done, setDone] = useState<{ message: string; bookingId?: string } | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadPrivateBookingData(ac: Academy) {
      try {
        setLoading(true);
        setError(null);
        setAcademy(ac);
        const locationsRes = await getPublicLocations(ac.id);
        if (cancelled) return;
        setPublicLocations(locationsRes.data ?? []);
        const cfg = await privateBookingService.getConfig(ac.id);
        if (cancelled) return;
        setConfig(cfg);
        if (!cfg.privateBookingEnabled) {
          setLoading(false);
          return;
        }
        const list = await privateBookingService.listCoaches(ac.id);
        if (cancelled) return;
        setCoaches(list);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (host.mode === 'subdomain') {
      if (hostLoading) return undefined;
      if (!hostAcademy) {
        setError('Academy not found');
        setLoading(false);
        return undefined;
      }
      void loadPrivateBookingData(hostAcademy);
      return () => {
        cancelled = true;
      };
    }

    if (!academySlug) return undefined;
    void (async () => {
      try {
        setLoading(true);
        setError(null);
        const ac = await academyService.getAcademyBySlug(academySlug);
        if (cancelled) return;
        await loadPrivateBookingData(ac);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [academySlug, host.mode, hostAcademy, hostLoading]);

  useEffect(() => {
    if (!academy?.id || !user) return undefined;
    if (!user.roles?.includes('guardian') && !user.roles?.includes('student')) return undefined;
    let cancelled = false;
    (async () => {
      const studsRes = await studentService.getStudents(academy.id);
      if (cancelled) return;
      const studs = studsRes.success && studsRes.data ? studsRes.data : [];
      setStudents(studs);
    })();
    return () => {
      cancelled = true;
    };
  }, [academy?.id, user]);

  const refreshStudents = useCallback(async () => {
    if (!academy?.id || !user) return;
    const studsRes = await studentService.getStudents(academy.id);
    const studs = studsRes.success && studsRes.data ? studsRes.data : [];
    setStudents(studs);
  }, [academy?.id, user]);

  const bookPrivateReturnPath = academy ? privateBookingRegisterPath(academy) : '';

  const handleRegisterSubmit = useCallback(
    async (data: UserFormData) => {
      if (!academySlug || !academy?.id || !bookPrivateReturnPath) return;
      try {
        setRegisterSubmitting(true);
        const { user: created, verificationEmailFailed } = await AuthService.signUpAsGuardian(
          data.email,
          data.password,
          data.firstName,
          data.lastName,
          bookPrivateReturnPath,
          {
            termsAccepted: data.termsAccepted,
            privacyAccepted: data.privacyAccepted,
            termsVersion: LEGAL_VERSIONS.TERMS,
            privacyVersion: LEGAL_VERSIONS.PRIVACY,
            marketingEmailConsent: data.marketingEmailConsent || false,
          },
          academy.id,
          'student',
          data.phone?.trim() ? { phone: data.phone.trim() } : undefined
        );
        setAuthUser(created);
        try {
          await academyProfileService.createProfile({
            academySlug,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            ...(data.phone?.trim() && { phone: data.phone.trim() }),
            ensureSelfStudentRosterEntry: true,
          });
        } catch {
          /* profile may already exist */
        }

        if (verificationEmailFailed) {
          navigate(buildEmailVerificationPageUrl({
            email: data.email,
            returnUrl: bookPrivateReturnPath,
            academySlug,
          }), {
            state: { verificationEmailSendFailed: true },
          });
          return;
        }

        toast.success('Account created. Continue below to choose your coach and time slots.');
      } catch (authError: unknown) {
        const err = authError as { code?: string; message?: string };
        if (err.code === 'auth/email-already-in-use') {
          toast.error('An account with this email already exists. Please sign in instead.');
          navigate(login(bookPrivateReturnPath), {
            state: {
              email: data.email,
              message: 'An account with this email already exists. Please sign in to continue.',
            },
          });
          return;
        }
        toast.error(err.message || 'Could not create account');
      } finally {
        setRegisterSubmitting(false);
      }
    },
    [academy?.id, academySlug, bookPrivateReturnPath, login, navigate, setAuthUser]
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error && !academy) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6">
        <p className="text-destructive mb-4">{error}</p>
        <Button variant="outline" onClick={() => navigate(p(''))}>
          Back
        </Button>
      </div>
    );
  }

  if (academy && isStarterRequestBookingAcademy(academy)) {
    return <StarterMemberAuthNotFound academy={academy} academySlug={academySlug} />;
  }

  if (config && !config.privateBookingEnabled && academy) {
    return (
      <PrivateBookingPublicLayout
        academy={academy}
        user={user}
        showMultiLocationPublic={showMultiLocationPublic}
        bannerError={bannerError}
        onBannerError={() => setBannerError(true)}
      >
        <div className="mx-auto max-w-lg py-12 text-center">
          <p className="mb-6 text-muted-foreground">Private lesson booking is not enabled for this academy.</p>
          <Button variant="outline" asChild>
            <Link to={p('')}>Back to academy home</Link>
          </Button>
        </div>
      </PrivateBookingPublicLayout>
    );
  }

  if (done && academy) {
    return (
      <PrivateBookingPublicLayout
        academy={academy}
        user={user}
        showMultiLocationPublic={showMultiLocationPublic}
        bannerError={bannerError}
        onBannerError={() => setBannerError(true)}
      >
        <div className="mx-auto max-w-lg py-8 text-center">
          <div
            className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/15 text-2xl text-primary"
            aria-hidden
          >
            ✓
          </div>
          <h2 className="text-2xl font-semibold tracking-tight">Thank you</h2>
          <p className="mt-3 text-muted-foreground leading-relaxed">{done.message}</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
            {done.bookingId && academySlug && (
              <Button asChild>
                <Link to={p('/bookings')}>Go to bookings</Link>
              </Button>
            )}
            <Button variant="outline" asChild>
              <Link to={p('')}>Academy home</Link>
            </Button>
          </div>
        </div>
      </PrivateBookingPublicLayout>
    );
  }

  if (!academy) {
    return null;
  }

  return (
    <PrivateBookingPublicLayout
      academy={academy}
      user={user}
      showMultiLocationPublic={showMultiLocationPublic}
      bannerError={bannerError}
      onBannerError={() => setBannerError(true)}
    >
      <div className="space-y-8">
          {!user && (
            <div className="grid grid-cols-1 gap-10 lg:gap-12 xl:grid-cols-12 xl:gap-10">
              <div className="min-w-0 xl:col-span-6">
                <Card className="shadow-sm lg:sticky lg:top-24">
                  <CardContent className="space-y-4 pt-10 text-sm leading-relaxed text-muted-foreground">
                    <p>
                      Create your account (or sign in) so we can link you to{' '}
                      <span className="font-medium text-foreground">{academy.name}</span>. Then you can finish your
                      booking in a few steps:
                    </p>
                    <ol className="list-decimal space-y-2 pl-5 marker:font-medium marker:text-foreground">
                      <li>Choose who the lessons are for and pick a coach.</li>
                      <li>Set session length and tap the schedule to choose one or more weekly time slots.</li>
                      <li>Review the price we calculate from their hourly rate, then submit your request.</li>
                    </ol>
                  </CardContent>
                </Card>
              </div>

              <div className="min-w-0 xl:col-span-6">
                <Card className="shadow-sm">
                  <CardHeader className="border-b border-border pb-5">
                    <h2 className="text-xl font-semibold tracking-tight text-foreground">Continue your booking</h2>
                    <CardDescription>
                      You need an account for this academy to book. Already have one?{' '}
                      <Link
                        to={login(bookPrivateReturnPath)}
                        className="font-medium text-primary underline underline-offset-2"
                      >
                        Sign in
                      </Link>
                      .
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6 pt-6">
                    <AcademyGuestRegistrationBlock
                      registerSubmitting={registerSubmitting}
                      form={registerForm}
                      onEmailSubmit={handleRegisterSubmit}
                      defaultCountrySource={{
                        country: academy.country,
                        region: academy.region,
                      }}
                    />
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {user && config && (
            <PrivateBookingWizard
              academy={academy}
              academySlug={academySlug!}
              config={config}
              coaches={coaches}
              students={students}
              onStudentsRefresh={refreshStudents}
              onComplete={setDone}
            />
          )}
      </div>
    </PrivateBookingPublicLayout>
  );
}
